import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { 
  Download, 
  FileText, 
  FileSpreadsheet, 
  Image,
  Calendar,
  Loader2
} from 'lucide-react';
import { PropBotConnectedAccount } from '@/hooks/use-prop-bot-detail-admin';
import { toast } from 'sonner';

interface AnalyticsExportToolsProps {
  botId: string;
  botName: string;
  analytics: any;
  connectedAccounts: PropBotConnectedAccount[];
}

export const AnalyticsExportTools: React.FC<AnalyticsExportToolsProps> = ({
  botId,
  botName,
  analytics,
  connectedAccounts
}) => {
  const [isExporting, setIsExporting] = useState(false);

  const generateTimestamp = () => {
    return new Date().toISOString().split('T')[0];
  };

  const exportToCSV = (data: any[], filename: string) => {
    if (!data || data.length === 0) {
      toast.error('No data available for export');
      return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          // Escape commas and quotes in CSV
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const generateAnalyticsReport = () => {
    const timestamp = generateTimestamp();
    
    const reportData = {
      reportInfo: {
        botId,
        botName,
        generatedAt: new Date().toISOString(),
        totalAccounts: connectedAccounts.length,
        totalCapital: analytics.totalCapital,
        averageBalance: analytics.averageBalance
      },
      summary: {
        activeUsers: analytics.activeUsersLast30Days,
        growthRate: analytics.growthRate,
        retentionRate: analytics.retentionRate,
        reversePositions: analytics.positionTypeDistribution.find(p => p.name === 'Reverse')?.value || 0,
        normalPositions: analytics.positionTypeDistribution.find(p => p.name === 'Normal')?.value || 0
      },
      accounts: connectedAccounts.map(acc => ({
        accountId: acc.id,
        userId: acc.userId,
        userName: acc.userName,
        userEmail: acc.userEmail,
        accountNumber: acc.accountNumber,
        balance: acc.balance || 0,
        volumeMultiplier: acc.volumeMultiplier,
        isLive: acc.isLive,
        isReversePosition: acc.isReversePosition,
        status: acc.status,
        broker: acc.broker || 'Unknown',
        propFirm: acc.propFirm || 'Unknown',
        addedDate: acc.addedDate,
        lastBalanceUpdate: acc.lastBalanceUpdate
      }))
    };

    return reportData;
  };

  const handleExportAnalyticsCSV = async () => {
    setIsExporting(true);
    try {
      const reportData = generateAnalyticsReport();
      const timestamp = generateTimestamp();
      
      // Export accounts data
      exportToCSV(
        reportData.accounts,
        `${botName.replace(/\s+/g, '_')}_analytics_${timestamp}.csv`
      );
      
      toast.success('Analytics data exported successfully');
    } catch (error) {
      toast.error('Failed to export analytics data');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportSummaryReport = async () => {
    setIsExporting(true);
    try {
      const reportData = generateAnalyticsReport();
      const timestamp = generateTimestamp();
      
      // Create summary report data
      const summaryData = [
        {
          metric: 'Total Accounts',
          value: reportData.reportInfo.totalAccounts,
          category: 'Overview'
        },
        {
          metric: 'Total Capital',
          value: `$${reportData.reportInfo.totalCapital.toLocaleString()}`,
          category: 'Financial'
        },
        {
          metric: 'Average Balance',
          value: `$${reportData.reportInfo.averageBalance.toLocaleString()}`,
          category: 'Financial'
        },
        {
          metric: 'Active Users (30d)',
          value: reportData.summary.activeUsers,
          category: 'Engagement'
        },
        {
          metric: 'Growth Rate',
          value: `${reportData.summary.growthRate.toFixed(1)}%`,
          category: 'Performance'
        },
        {
          metric: 'Retention Rate',
          value: `${reportData.summary.retentionRate.toFixed(1)}%`,
          category: 'Performance'
        },
        {
          metric: 'Normal Positions',
          value: reportData.summary.normalPositions,
          category: 'Position Types'
        },
        {
          metric: 'Reverse Positions',
          value: reportData.summary.reversePositions,
          category: 'Position Types'
        }
      ];
      
      exportToCSV(
        summaryData,
        `${botName.replace(/\s+/g, '_')}_summary_${timestamp}.csv`
      );
      
      toast.success('Summary report exported successfully');
    } catch (error) {
      toast.error('Failed to export summary report');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportTopUsers = async () => {
    setIsExporting(true);
    try {
      const topUsersData = analytics.topUsers.map((user: any, index: number) => ({
        rank: index + 1,
        userName: user.userName,
        userEmail: user.userEmail,
        totalCapital: user.totalCapital,
        accountCount: user.accountCount,
        averageBalance: user.totalCapital / user.accountCount,
        lastActivity: user.lastActivity
      }));
      
      const timestamp = generateTimestamp();
      exportToCSV(
        topUsersData,
        `${botName.replace(/\s+/g, '_')}_top_users_${timestamp}.csv`
      );
      
      toast.success('Top users data exported successfully');
    } catch (error) {
      toast.error('Failed to export top users data');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportCapitalDistribution = async () => {
    setIsExporting(true);
    try {
      const capitalData = [
        ...analytics.capitalDistribution.map((item: any) => ({
          type: 'Account Type',
          category: item.name,
          value: item.value,
          count: item.count || 0
        })),
        ...analytics.positionTypeDistribution.map((item: any) => ({
          type: 'Position Type',
          category: item.name,
          value: item.value,
          count: 0
        })),
        ...analytics.brokerDistribution.map((item: any) => ({
          type: 'Broker',
          category: item.name,
          value: item.value,
          count: 0
        }))
      ];
      
      const timestamp = generateTimestamp();
      exportToCSV(
        capitalData,
        `${botName.replace(/\s+/g, '_')}_distribution_${timestamp}.csv`
      );
      
      toast.success('Capital distribution data exported successfully');
    } catch (error) {
      toast.error('Failed to export capital distribution data');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={isExporting}>
          {isExporting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Export Analytics
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={handleExportAnalyticsCSV}>
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Complete Analytics (CSV)
        </DropdownMenuItem>
        
        <DropdownMenuItem onClick={handleExportSummaryReport}>
          <FileText className="h-4 w-4 mr-2" />
          Summary Report (CSV)
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem onClick={handleExportTopUsers}>
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Top Users Data
        </DropdownMenuItem>
        
        <DropdownMenuItem onClick={handleExportCapitalDistribution}>
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Capital Distribution
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem disabled>
          <Image className="h-4 w-4 mr-2" />
          Export Charts (Coming Soon)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};